import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { useYear } from '../year/useYear'
import { yearOfPeriod } from '../lib/format'
import { Loading, ErrorState } from '../components/States'
import { LanguageToggle } from '../components/LanguageToggle'
import { Logo } from '../components/Logo'

interface YearInfo {
  year: number
  /** Nombre de loyers et de depenses enregistres sur l'annee. */
  charges: number
  expenses: number
  /** Baux couvrant tout ou partie de l'annee. */
  leases: number
}

/**
 * Les annees proposees sont deduites des donnees, pas d'une liste
 * figee : celles qui portent des loyers, des depenses ou des baux, plus
 * l'annee en cours pour pouvoir commencer une annee vide.
 */
async function loadYears(): Promise<YearInfo[]> {
  const [charges, expenses, leases] = await Promise.all([
    supabase.from('rent_charges').select('period'),
    supabase.from('expenses').select('period'),
    supabase.from('leases').select('start_date, end_date'),
  ])

  if (charges.error) throw charges.error
  if (expenses.error) throw expenses.error
  if (leases.error) throw leases.error

  const info = new Map<number, YearInfo>()
  const touch = (y: number): YearInfo => {
    const cur = info.get(y) ?? { year: y, charges: 0, expenses: 0, leases: 0 }
    info.set(y, cur)
    return cur
  }

  for (const c of (charges.data ?? []) as { period: string }[]) {
    touch(yearOfPeriod(c.period)).charges += 1
  }
  for (const e of (expenses.data ?? []) as { period: string }[]) {
    touch(yearOfPeriod(e.period)).expenses += 1
  }

  // Un bail compte pour chaque annee qu'il recouvre, meme sans loyer
  // encore enregistre : c'est ainsi qu'une annee a preparer apparait.
  const maintenant = new Date().getFullYear()
  for (const l of (leases.data ?? []) as {
    start_date: string | null
    end_date: string | null
  }[]) {
    const debut = l.start_date ? Number(l.start_date.slice(0, 4)) : maintenant
    const fin = l.end_date ? Number(l.end_date.slice(0, 4)) : maintenant
    for (let y = Math.min(debut, fin); y <= Math.max(debut, fin); y++) {
      if (y >= 2000 && y <= 2100) touch(y).leases += 1
    }
  }

  // L'annee en cours est toujours proposee, meme vide.
  touch(maintenant)

  return [...info.values()].sort((a, b) => b.year - a.year)
}

export function YearScreen() {
  const { t } = useI18n()
  const { profile, signOut } = useAuth()
  const { setYear } = useYear()
  const { data, loading, error, reload } = useAsync(loadYears)

  const courante = new Date().getFullYear()

  return (
    <div className="yearpick">
      <div className="yearpick__top">
        <LanguageToggle />
      </div>

      <div className="yearpick__body">
        <Logo onDark />
        {/* Nom issu de la base, jamais traduit. */}
        <p className="yearpick__hello">{profile?.full_name ?? ""}</p>

        <div className="card">
          <h2>{t('year_choose')}</h2>
          <p className="muted">{t('year_choose_body')}</p>

          {loading && <Loading />}
          {error && <ErrorState message={error} onRetry={reload} />}

          {data && (
            <div className="yearpick__grid">
              {data.map((y) => (
                <button
                  key={y.year}
                  type="button"
                  className={`yearbtn ${y.year === courante ? 'is-current' : ''}`}
                  onClick={() => setYear(y.year)}
                >
                  <span className="yearbtn__year">{y.year}</span>
                  <span className="yearbtn__meta">
                    {y.charges > 0
                      ? t('year_has_rent', { n: y.charges })
                      : y.leases > 0
                        ? t('year_has_leases', { n: y.leases })
                        : t('year_empty')}
                  </span>
                  {y.year === courante && (
                    <span className="yearbtn__tag">{t('year_current')}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <button type="button" className="btn btn--ghost" onClick={() => void signOut()}>
          {t('logout')}
        </button>
      </div>
    </div>
  )
}
